import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getActiveChallenges, getCompletedChallenges, ChallengeData } from '../services/challengeService';
import { Swords, Trophy, Download, Share2, Target, Frown, X, Plus } from 'lucide-react';
import html2canvas from 'html2canvas';
import { getAvatarUrl } from '../utils/avatarUtils';
import ChallengeShareCard from '../components/social/ChallengeShareCard';

import { SafeImage } from '../components/common/SafeImage';

export default function ChallengesPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeChallenges, setActiveChallenges] = useState<ChallengeData[]>([]);
  const [completedChallenges, setCompletedChallenges] = useState<ChallengeData[]>([]);
  const [sharingChallenge, setSharingChallenge] = useState<ChallengeData | null>(null);

  const shareCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!currentUser) return;
    
    const unsubActiveChallenges = getActiveChallenges(currentUser.uid, setActiveChallenges);
    const unsubCompletedChallenges = getCompletedChallenges(currentUser.uid, setCompletedChallenges);
    
    return () => {
      unsubActiveChallenges();
      unsubCompletedChallenges();
    };
  }, [currentUser]);

  const handleProcessChallengeImage = async (challenge: ChallengeData, action: 'share' | 'download' = 'share') => {
    setSharingChallenge(challenge);
    
    // Give React time to render the updated state in the hidden div
    setTimeout(async () => {
      if (!shareCardRef.current) return;
      
      try {
        const imgs = shareCardRef.current.querySelectorAll('img');
        imgs.forEach((img) => { 
          img.crossOrigin = 'anonymous'; 
        });

        const canvas = await html2canvas(shareCardRef.current, { 
          useCORS: true,
          scale: 2, 
          backgroundColor: null,
          scrollX: 0,
          scrollY: 0,
          windowWidth: document.documentElement.scrollWidth,
          windowHeight: document.documentElement.scrollHeight
        });
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          
          if (action === 'share') {
            const file = new File([blob], `challenge-${challenge.id}.png`, { type: 'image/png' });
            
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
              try {
                const myName = currentUser?.uid === challenge.challengerId ? challenge.challengerName : challenge.challengedName;
                await navigator.share({
                  title: 'Challenge',
                  text: `Check out this challenge on LinguaLearn! Add me as a friend: ${myName || ''}\nhttps://lingualearn.web.app/`,
                  files: [file]
                });
                setSharingChallenge(null);
                return;
              } catch (err) {
                console.log('Share cancelled or failed', err);
              }
            }
          }
          
          // Download (explicitly requested or fallback if share fails/unsupported)
          const image = canvas.toDataURL("image/png");
          const link = document.createElement('a');
          link.href = image;
          link.download = `challenge-${challenge.id}.png`;
          link.click();
          setSharingChallenge(null);
        });
      } catch (e) {
        console.error("Failed to generate image", e);
        setSharingChallenge(null);
      }
    }, 100);
  };

  if (!currentUser) return null;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 pb-20 md:pb-8">
      <div className="mb-8 border-b pb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center gap-2">
            <Swords className="text-indigo-600 w-8 h-8" /> Challenges
          </h1>
          <p className="text-gray-500 mt-2">Track your active competitions and past victories.</p>
        </div>
        <button
          onClick={() => navigate('/social')}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg shrink-0"
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">New Challenge</span>
        </button>
      </div>

      <div className="space-y-12">
        {/* Active Challenges */}
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-6">
            <Target className="text-indigo-500 w-6 h-6" /> Active Challenges
          </h2>
          {activeChallenges.length === 0 ? (
            <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center">
              <Swords className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No active challenges.</p>
              <p className="text-sm text-gray-400 mt-1">Go to the Friends page to challenge someone!</p>
              <button 
                onClick={() => navigate('/social')}
                className="mt-4 px-6 py-2 bg-indigo-100 text-indigo-700 font-semibold rounded-full hover:bg-indigo-200 transition-colors"
              >
                Find Friends to Challenge
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {activeChallenges.map(challenge => {
                const isChallenger = currentUser.uid === challenge.challengerId;
                const myScore = Math.round(isChallenger ? challenge.challengerScore : challenge.challengedScore);
                const theirScore = Math.round(isChallenger ? challenge.challengedScore : challenge.challengerScore);
                const theirName = isChallenger ? challenge.challengedName : challenge.challengerName;
                const theirPhoto = isChallenger ? challenge.challengedPhoto : challenge.challengerPhoto;
                
                const timeLeftMs = challenge.endTime - Date.now();
                const hoursLeft = Math.floor(timeLeftMs / (1000 * 60 * 60));
                const minsLeft = Math.floor((timeLeftMs % (1000 * 60 * 60)) / (1000 * 60));
                
                const isWinning = myScore > theirScore;
                const isLosing = myScore < theirScore;
                
                const typeLabel = challenge.challengeType === 'crime_game' ? 'Crime Game' :
                                  challenge.challengeType === 'daily_training' ? 'Training' :
                                  challenge.challengeType === 'word_of_day' ? 'Word of Day' : 'Point Race';
                
                const myPhoto = isChallenger ? challenge.challengerPhoto : challenge.challengedPhoto;
                const myAvatarUrl = myPhoto || currentUser.photoURL || getAvatarUrl(currentUser.uid);
                const theirAvatarUrl = theirPhoto || getAvatarUrl(theirName || 'opponent');

                return (
                  <div 
                    key={challenge.id} 
                    onClick={() => navigate(`/challenges/${challenge.id}`)}
                    className="relative overflow-hidden rounded-2xl border-2 border-indigo-100 bg-white p-6 shadow-md hover:shadow-lg transition-all cursor-pointer"
                  >
                    <div className="absolute bottom-0 left-0 h-1.5 bg-gray-100 w-full">
                      <div 
                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-500" 
                        style={{ width: `${Math.max(5, (timeLeftMs / (24 * 60 * 60 * 1000)) * 100)}%` }}
                      />
                    </div>
                    
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-lg border border-indigo-100">
                        24h {typeLabel} • {new Date(challenge.startTime).toLocaleDateString('pt-BR')}
                      </span>
                      <span className="text-sm font-semibold text-gray-500 flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-lg">
                        ⏱ {hoursLeft}h {minsLeft}m left
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between mt-6 mb-4">
                      <div className="flex flex-col items-center flex-1">
                        <SafeImage 
                          src={myAvatarUrl} 
                          fallbackSeed={currentUser.uid}
                          className={`w-16 h-16 rounded-full object-cover shadow-sm border-4 ${isWinning ? 'border-green-400' : 'border-gray-100'}`} 
                          alt="You" 
                        />
                        <span className="font-bold text-3xl mt-3 text-gray-800">{myScore}</span>
                        <span className="text-sm font-medium text-gray-500 truncate w-full text-center mt-1">You</span>
                      </div>
                      
                      <div className="flex flex-col items-center px-4">
                        <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mb-2">
                           <Swords className="w-5 h-5 text-gray-400" />
                        </div>
                        {isWinning && <span className="text-xs font-bold text-green-500 uppercase tracking-wide">Winning!</span>}
                        {isLosing && <span className="text-xs font-bold text-red-500 uppercase tracking-wide">Trailing</span>}
                        {!isWinning && !isLosing && <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Tied</span>}
                      </div>
                      
                      <div className="flex flex-col items-center flex-1">
                        <SafeImage 
                          src={theirAvatarUrl} 
                          fallbackSeed={theirName || 'opponent'}
                          className={`w-16 h-16 rounded-full object-cover shadow-sm border-4 ${isLosing ? 'border-green-400' : 'border-gray-100'}`} 
                          alt={theirName || 'Opponent'} 
                        />
                        <span className="font-bold text-3xl mt-3 text-gray-800">{theirScore}</span>
                        <span className="text-sm font-medium text-gray-500 truncate w-full text-center mt-1">{theirName}</span>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-gray-100 flex gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (challenge.challengeType === 'crime_game') navigate('/crime-game');
                          else if (challenge.challengeType === 'daily_training') navigate('/training');
                          else if (challenge.challengeType === 'word_of_day') navigate('/day');
                          else navigate('/dashboard');
                        }}
                        className="flex-1 py-2.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all shadow-sm hover:shadow"
                      >
                        Go Play
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProcessChallengeImage(challenge, 'download');
                        }}
                        className="p-2.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl transition-colors flex items-center justify-center border border-blue-100"
                        title="Download Image"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProcessChallengeImage(challenge, 'share');
                        }}
                        className="p-2.5 bg-pink-50 text-pink-600 hover:bg-pink-100 rounded-xl transition-colors flex items-center justify-center border border-pink-100"
                        title="Share Challenge"
                      >
                        <Share2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Completed Challenges */}
        <div>
          <div className="flex justify-between items-center mb-6 border-b pb-2">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Trophy className="text-yellow-500 w-6 h-6" /> History
            </h2>
            <div className="flex gap-3">
              <span className="px-4 py-1.5 bg-green-100 text-green-700 text-sm font-bold rounded-lg shadow-sm flex items-center gap-1.5">
                <Trophy className="w-4 h-4" />
                {completedChallenges.filter(c => c.winnerId === currentUser.uid).length} Victories
              </span>
              <span className="px-4 py-1.5 bg-red-100 text-red-700 text-sm font-bold rounded-lg shadow-sm flex items-center gap-1.5">
                <Frown className="w-4 h-4" />
                {completedChallenges.filter(c => c.winnerId && c.winnerId !== currentUser.uid).length} Defeats
              </span>
            </div>
          </div>
          {completedChallenges.length === 0 ? (
            <p className="text-gray-500 italic text-sm">No completed challenges yet.</p>
          ) : (
            <div className="space-y-4">
              {completedChallenges.map(challenge => {
                const isChallenger = currentUser.uid === challenge.challengerId;
                const myScore = Math.round(isChallenger ? challenge.challengerScore : challenge.challengedScore);
                const theirScore = Math.round(isChallenger ? challenge.challengedScore : challenge.challengerScore);
                const theirName = isChallenger ? challenge.challengedName : challenge.challengerName;
                const iWon = challenge.winnerId === currentUser.uid;
                const isDraw = !challenge.winnerId;
                const typeLabel = challenge.challengeType === 'crime_game' ? 'Crime Game' :
                                  challenge.challengeType === 'daily_training' ? 'Training' :
                                  challenge.challengeType === 'word_of_day' ? 'Word of Day' : 'Point Race';
                
                return (
                  <div 
                    key={challenge.id} 
                    onClick={() => navigate(`/challenges/${challenge.id}`)}
                    className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm flex items-center justify-between hover:border-gray-300 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-5">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 shadow-inner ${iWon ? 'bg-green-100 text-green-600' : isDraw ? 'bg-gray-100 text-gray-500' : 'bg-red-100 text-red-600'}`}>
                        {iWon ? <Trophy className="w-7 h-7" /> : isDraw ? <Swords className="w-7 h-7" /> : <Frown className="w-7 h-7" />}
                      </div>
                      <div>
                        <p className="font-bold text-lg text-gray-800 mb-1">
                          You vs {theirName} 
                        </p>
                        <div className="flex items-center gap-3">
                          <p className="text-sm font-medium">
                            {iWon ? <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded">Victory</span> : isDraw ? <span className="text-gray-600 bg-gray-100 px-2 py-0.5 rounded">Draw</span> : <span className="text-red-600 bg-red-50 px-2 py-0.5 rounded">Defeat</span>}
                          </p>
                          <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {typeLabel} • {new Date(challenge.startTime).toLocaleDateString('pt-BR')}
                          </span>
                          <span className="text-gray-400 text-sm mx-1">•</span>
                          <p className="text-sm font-bold text-gray-700">
                            Score: {myScore} - {theirScore}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProcessChallengeImage(challenge, 'download');
                        }}
                        className="p-2.5 text-blue-500 hover:bg-blue-50 rounded-xl transition-colors border border-transparent hover:border-blue-100"
                        title="Download Image"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProcessChallengeImage(challenge, 'share');
                        }}
                        className="p-2.5 text-pink-500 hover:bg-pink-50 rounded-xl transition-colors border border-transparent hover:border-pink-100"
                        title="Share Challenge"
                      >
                        <Share2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>



      {/* Hidden Share Card for Image Generation */}
      <ChallengeShareCard challenge={sharingChallenge} currentUser={currentUser} cardRef={shareCardRef} />
    </div>
  );
}
